/**
 * VA Dashboard — /va route
 * ────────────────────────
 * Standalone operational dashboard for Emily's VA team. Bolts onto Crew Chief
 * at /va but bypasses the Jamie-facing sidebar/header entirely.
 *
 * Six widgets (per VA_DASHBOARD_SPEC_v2.md):
 *   A. CallListWidget       — today's phone-rich leads with preview URL copy
 *   B. TriggerScrapeWidget  — VA-triggered multi-city/niche scrape
 *   C. MRRWidget            — current MRR snapshot
 *   C+. MRRTrendWidget      — 6-month MRR trend chart
 *   D. HandoffQueueWidget   — VA's hot handoffs with Jamie's status
 *   E. OutreachTrackerWidget — daily activity: emailed + called + previews
 *
 * VA identity: prompt-once localStorage. No auth wiring needed.
 * All backend endpoints already shipped (migration 62, admin_router.py, accounts_router.py).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Phone, Copy, ExternalLink, Search, RefreshCw, Loader2,
  DollarSign, TrendingUp, TrendingDown, AlertCircle, CheckCircle,
  Flame, Send, MessageSquare, User, X,
} from 'lucide-react';

// ─── CONFIG ─────────────────────────────────────────────────────────────────
const API    = process.env.REACT_APP_API_URL || 'https://aria-api-ibja.onrender.com';
const APIKEY = process.env.REACT_APP_API_KEY || '';
const SOURCEPOINT_OFFER_SLUG = 'sourcepoint_website_mrr';

// Outcome codes exactly as defined server-side (VA_OUTCOME_CODES in admin_router.py)
const OUTCOME_CODES = [
  { code: 'TEXTED_PREVIEW',            label: 'Texted preview URL',   hot: false },
  { code: 'INTERESTED_SENT_CALENDAR',  label: 'Interested — sent calendar', hot: true },
  { code: 'INTERESTED_QUOTE_REQUESTED', label: 'Interested — quote requested', hot: true },
  { code: 'HOT_HANDOFF_JAMIE',         label: '🔥 Hot handoff → Jamie', hot: true },
  { code: 'NOT_INTERESTED',            label: 'Not interested',       hot: false },
  { code: 'HAS_WEBSITE_CONFIRMED',     label: 'Already has website',  hot: false },
  { code: 'WRONG_NUMBER',              label: 'Wrong number',         hot: false },
  { code: 'WRONG_PERSON',              label: 'Wrong person',         hot: false },
  { code: 'VM_LEFT_1',                 label: 'Voicemail #1',         hot: false },
  { code: 'VM_LEFT_2',                 label: 'Voicemail #2',         hot: false },
  { code: 'NO_ANSWER_1',               label: 'No answer #1',         hot: false },
  { code: 'NO_ANSWER_2',               label: 'No answer #2',         hot: false },
  { code: 'NO_ANSWER_3',               label: 'No answer #3',         hot: false },
  { code: 'CALLBACK',                  label: 'Callback scheduled',   hot: false },
  { code: 'DO_NOT_CALL',               label: 'DO NOT CALL',          hot: false },
];

// ─── API CLIENT ──────────────────────────────────────────────────────────────
async function api(path, opts = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...(APIKEY ? { 'X-ARIA-API-Key': APIKEY, 'X-API-Key': APIKEY } : {}),
    ...opts.headers,
  };
  try {
    const r = await fetch(`${API}${path}`, { ...opts, headers });
    if (!r.ok) {
      const err = await r.json().catch(() => ({ detail: r.statusText }));
      throw new Error(err.detail || `HTTP ${r.status}`);
    }
    return { data: await r.json(), error: null };
  } catch (e) {
    return { data: null, error: e.message };
  }
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const money = (n) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Number(n).toFixed(0)}`;
};

const timeAgo = (iso) => {
  if (!iso) return '—';
  const mins = Math.round((Date.now() - new Date(iso)) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
};

// Parse CSV → array of row objects (handles quoted fields with commas)
function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  const headers = splitCSVLine(lines[0]);
  return lines.slice(1).map((line) => {
    const fields = splitCSVLine(line);
    const row = {};
    headers.forEach((h, i) => { row[h] = fields[i] ?? ''; });
    return row;
  });
}
function splitCSVLine(line) {
  const out = [];
  let cur = '', inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuote && line[i + 1] === '"') { cur += '"'; i++; }
      else inQuote = !inQuote;
    } else if (ch === ',' && !inQuote) {
      out.push(cur); cur = '';
    } else cur += ch;
  }
  out.push(cur);
  return out;
}

// ─── VA IDENTITY — prompt-once localStorage ──────────────────────────────────
function useVAIdentity() {
  const [vaId, setVaId] = useState(() => localStorage.getItem('va_id') || '');
  const setAndPersist = (id) => {
    localStorage.setItem('va_id', id);
    setVaId(id);
  };
  return [vaId, setAndPersist];
}

function VAIdentityGate({ onSet }) {
  const [name, setName] = useState('');
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
            <User className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">Who's calling today?</h2>
            <p className="text-xs text-slate-500">One-time setup — saved on this device.</p>
          </div>
        </div>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && name.trim() && onSet(name.trim())}
          placeholder="Your name or email"
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          autoFocus
        />
        <button
          onClick={() => name.trim() && onSet(name.trim())}
          disabled={!name.trim()}
          className="mt-3 w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white rounded-lg py-2 text-sm font-medium"
        >
          Start calling
        </button>
      </div>
    </div>
  );
}

// ─── UI PRIMITIVES ───────────────────────────────────────────────────────────
function Card({ children, className = '' }) {
  return <div className={`bg-white rounded-2xl border border-slate-200 shadow-sm ${className}`}>{children}</div>;
}
function CardHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
      <div>
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
function Spinner() { return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />; }
function TierBadge({ score }) {
  const s = Number(score) || 0;
  if (s >= 70) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">T1 · {s}</span>;
  if (s >= 50) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">T2 · {s}</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">T3 · {s}</span>;
}

// ─── TOAST ───────────────────────────────────────────────────────────────────
function useToasts() {
  const [items, setItems] = useState([]);
  const push = useCallback((title, type = 'info') => {
    const id = Date.now() + Math.random();
    setItems((p) => [...p, { id, title, type }]);
    setTimeout(() => setItems((p) => p.filter((t) => t.id !== id)), 4000);
  }, []);
  return [items, push];
}
function Toasts({ items }) {
  const cls = {
    success: 'bg-emerald-600 text-white',
    error: 'bg-red-600 text-white',
    info: 'bg-slate-800 text-white',
  };
  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2">
      {items.map((t) => (
        <div key={t.id} className={`px-4 py-2 rounded-lg shadow-lg text-sm ${cls[t.type] || cls.info}`}>{t.title}</div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION A — CallListWidget
// ═══════════════════════════════════════════════════════════════════════════
function CallListWidget({ vaId, toast, refreshTrigger, onLog }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ tier: 'all', city: '', niche: '', search: '', untouched: true });
  const [selected, setSelected] = useState(null); // lead for log modal

  const load = useCallback(async () => {
    setLoading(true);
    const url = `${API}/api/admin/va-call-list.csv?min_score=30&limit=500`;
    try {
      const r = await fetch(url, {
        headers: APIKEY ? { 'X-ARIA-API-Key': APIKEY, 'X-API-Key': APIKEY } : {},
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const csv = await r.text();
      const parsed = parseCSV(csv);
      setRows(parsed);
    } catch (e) {
      toast(`Call list failed: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load, refreshTrigger]);
  // Auto-refresh every 5 min
  useEffect(() => {
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  // Unique cities / niches for filter chips
  const cities = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => r.city && set.add(r.city));
    return Array.from(set).sort();
  }, [rows]);
  const niches = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => r.niche && set.add(r.niche));
    return Array.from(set).sort();
  }, [rows]);

  // Apply filters
  const visible = useMemo(() => {
    return rows.filter((r) => {
      const score = Number(r.wedge_score) || 0;
      if (filters.tier === 't1' && score < 70) return false;
      if (filters.tier === 't2' && (score < 50 || score >= 70)) return false;
      if (filters.city && r.city !== filters.city) return false;
      if (filters.niche && r.niche !== filters.niche) return false;
      if (filters.search) {
        const q = filters.search.toLowerCase();
        if (!(r.company || '').toLowerCase().includes(q) && !(r.phone || '').includes(q)) return false;
      }
      return true;
    });
  }, [rows, filters]);

  const copyPreview = async (url, company) => {
    try {
      await navigator.clipboard.writeText(url);
      toast(`Preview URL copied for ${company}`, 'success');
    } catch {
      toast('Copy failed — select the URL manually', 'error');
    }
  };

  return (
    <Card>
      <CardHeader
        title="Today's Call List"
        subtitle={`${visible.length} of ${rows.length} leads · sorted by wedge score`}
        action={
          <button onClick={load} disabled={loading} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        }
      />

      {/* Filters */}
      <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap gap-2 items-center bg-slate-50">
        {['all', 't1', 't2'].map((t) => (
          <button
            key={t}
            onClick={() => setFilters((f) => ({ ...f, tier: t }))}
            className={`px-3 py-1 rounded-full text-xs font-medium border ${
              filters.tier === t ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200'
            }`}
          >
            {t === 'all' ? 'All' : t === 't1' ? 'Tier 1' : 'Tier 2'}
          </button>
        ))}
        <select value={filters.city} onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))} className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white">
          <option value="">All cities</option>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filters.niche} onChange={(e) => setFilters((f) => ({ ...f, niche: e.target.value }))} className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white">
          <option value="">All niches</option>
          {niches.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <div className="flex items-center gap-1 ml-auto">
          <Search className="w-3.5 h-3.5 text-slate-400" />
          <input
            value={filters.search}
            onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            placeholder="Search company or phone"
            className="text-xs border border-slate-200 rounded-lg px-2 py-1 w-44 bg-white"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading && rows.length === 0 ? (
          <div className="p-10 flex justify-center"><Spinner /></div>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">No leads match these filters.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="text-left px-5 py-2 font-medium">Company</th>
                <th className="text-left px-3 py-2 font-medium">Phone</th>
                <th className="text-left px-3 py-2 font-medium">City / Niche</th>
                <th className="text-left px-3 py-2 font-medium">Score</th>
                <th className="text-left px-3 py-2 font-medium">Preview</th>
                <th className="text-right px-5 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map((r, i) => (
                <tr key={r.id || i} className="hover:bg-slate-50">
                  <td className="px-5 py-3">
                    <a href={r.preview_url} target="_blank" rel="noopener noreferrer" className="font-medium text-slate-800 hover:text-indigo-600 flex items-center gap-1">
                      {r.company || '—'}
                      {r.preview_url && <ExternalLink className="w-3 h-3 opacity-50" />}
                    </a>
                    {r.name && <div className="text-xs text-slate-400">{r.name}</div>}
                  </td>
                  <td className="px-3 py-3">
                    {r.phone ? (
                      <a href={`tel:${r.phone.replace(/[^\d+]/g, '')}`} className="text-indigo-600 hover:underline flex items-center gap-1">
                        <Phone className="w-3.5 h-3.5" /> {r.phone}
                      </a>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-3 text-xs text-slate-600">
                    {r.city || '—'}<br/>
                    <span className="text-slate-400">{r.niche || '—'}</span>
                  </td>
                  <td className="px-3 py-3"><TierBadge score={r.wedge_score} /></td>
                  <td className="px-3 py-3">
                    {r.preview_url ? (
                      <button
                        onClick={() => copyPreview(r.preview_url, r.company)}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
                        title="Copy preview URL"
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => setSelected(r)}
                      className="text-xs px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      Log outcome
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <LogOutcomeModal
          lead={selected}
          vaId={vaId}
          onClose={() => setSelected(null)}
          onLogged={() => { setSelected(null); load(); onLog && onLog(); }}
          toast={toast}
        />
      )}
    </Card>
  );
}

function LogOutcomeModal({ lead, vaId, onClose, onLogged, toast }) {
  const [code, setCode] = useState('TEXTED_PREVIEW');
  const [note, setNote] = useState('');
  const [callbackAt, setCallbackAt] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (code === 'CALLBACK' && !callbackAt) {
      toast('Pick a callback date/time', 'error'); return;
    }
    setSaving(true);
    const body = { lead_id: Number(lead.id), va_id: vaId, code, note };
    if (code === 'CALLBACK') body.callback_at = new Date(callbackAt).toISOString();
    const { data, error } = await api('/api/admin/log-va-call-outcome', {
      method: 'POST', body: JSON.stringify(body),
    });
    setSaving(false);
    if (error) { toast(`Log failed: ${error}`, 'error'); return; }
    toast(code === 'HOT_HANDOFF_JAMIE' ? '🔥 Handoff sent to Jamie' : 'Logged', 'success');
    onLogged();
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-slate-900">Log outcome</h3>
            <p className="text-xs text-slate-500 mt-0.5">{lead.company} · {lead.phone}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2 block">Outcome</label>
            <div className="grid grid-cols-1 gap-1 max-h-64 overflow-y-auto pr-1">
              {OUTCOME_CODES.map((o) => (
                <label key={o.code} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer ${code === o.code ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-slate-50 border border-transparent'}`}>
                  <input type="radio" name="code" value={o.code} checked={code === o.code} onChange={() => setCode(o.code)} className="text-indigo-600" />
                  <span className={o.hot ? 'text-slate-800 font-medium' : 'text-slate-600'}>{o.label}</span>
                </label>
              ))}
            </div>
          </div>
          {code === 'CALLBACK' && (
            <div>
              <label className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2 block">Callback at</label>
              <input type="datetime-local" value={callbackAt} onChange={(e) => setCallbackAt(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2 block">Note (optional)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Anything Emily/Jamie should know…" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm resize-none" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-slate-100 flex gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50">
            {saving ? 'Saving…' : 'Log'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION B — TriggerScrapeWidget
// ═══════════════════════════════════════════════════════════════════════════
const CITIES_PRESET = ['Denver', 'Phoenix', 'Salt Lake City', 'Boise', 'Austin', 'Nashville'];
const NICHES_PRESET = ['roofers', 'plumbers', 'electricians', 'hvac', 'landscapers', 'painters'];

function TriggerScrapeWidget({ toast, onScraped }) {
  const [cities, setCities] = useState(['Denver']);
  const [niches, setNiches] = useState(['roofers']);
  const [customCity, setCustomCity] = useState('');
  const [customNiche, setCustomNiche] = useState('');
  const [limit, setLimit] = useState(30);
  const [running, setRunning] = useState(false);

  // Client-side rate limit: 3 scrapes per hour per VA
  const rateLimited = useMemo(() => {
    const history = JSON.parse(localStorage.getItem('scrape_history') || '[]');
    const recent = history.filter((ts) => Date.now() - ts < 60 * 60 * 1000);
    return recent.length >= 3;
  }, [running]); // recompute after each scrape

  const toggle = (arr, setArr, val) => {
    setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  const submit = async () => {
    if (rateLimited) { toast('3 scrapes/hour limit — wait a bit before another', 'error'); return; }
    const finalCities = [...cities, customCity].filter(Boolean).map((c) => c.trim());
    const finalNiches = [...niches, customNiche].filter(Boolean).map((n) => n.trim());
    if (!finalCities.length || !finalNiches.length) {
      toast('Pick at least one city and one niche', 'error'); return;
    }
    setRunning(true);
    const endpoint = finalCities.length === 1 && finalNiches.length === 1
      ? '/api/admin/scrape-google-maps'
      : '/api/admin/run-maps-discovery';
    const body = finalCities.length === 1 && finalNiches.length === 1
      ? { city: finalCities[0], niche: finalNiches[0], limit }
      : { cities: finalCities, niches: finalNiches, limit };
    const { data, error } = await api(endpoint, { method: 'POST', body: JSON.stringify(body) });
    setRunning(false);
    if (error) { toast(`Scrape failed: ${error}`, 'error'); return; }

    // Track for rate limit
    const history = JSON.parse(localStorage.getItem('scrape_history') || '[]');
    localStorage.setItem('scrape_history', JSON.stringify([...history, Date.now()].slice(-10)));

    const found = data.imported ?? data.total_imported ?? 0;
    toast(`Found ${found} new leads. Refreshing call list…`, 'success');
    onScraped && onScraped();
  };

  return (
    <Card>
      <CardHeader title="Trigger New Scrape" subtitle="Find fresh Google Maps leads for a city + niche combo" />
      <div className="p-5 space-y-4">
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2 block">Cities</label>
          <div className="flex flex-wrap gap-1.5">
            {CITIES_PRESET.map((c) => (
              <button key={c} onClick={() => toggle(cities, setCities, c)} className={`px-3 py-1 rounded-full text-xs border ${cities.includes(c) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200'}`}>
                {c}
              </button>
            ))}
            <input value={customCity} onChange={(e) => setCustomCity(e.target.value)} placeholder="+ custom" className="px-3 py-1 rounded-full text-xs border border-slate-200 w-28" />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2 block">Niches</label>
          <div className="flex flex-wrap gap-1.5">
            {NICHES_PRESET.map((n) => (
              <button key={n} onClick={() => toggle(niches, setNiches, n)} className={`px-3 py-1 rounded-full text-xs border ${niches.includes(n) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200'}`}>
                {n}
              </button>
            ))}
            <input value={customNiche} onChange={(e) => setCustomNiche(e.target.value)} placeholder="+ custom" className="px-3 py-1 rounded-full text-xs border border-slate-200 w-28" />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-2 block">Results per city × niche: <span className="text-slate-900 font-semibold">{limit}</span></label>
          <input type="range" min={10} max={100} step={5} value={limit} onChange={(e) => setLimit(Number(e.target.value))} className="w-full" />
        </div>
        <div className="flex items-center justify-between">
          {rateLimited && (
            <p className="text-xs text-amber-700 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Rate limit reached — wait an hour.</p>
          )}
          <button onClick={submit} disabled={running || rateLimited} className="ml-auto inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white px-4 py-2 rounded-lg text-sm font-medium">
            {running ? <><Loader2 className="w-4 h-4 animate-spin" /> Scraping (30–90s)…</> : <><Search className="w-4 h-4" /> Find new leads</>}
          </button>
        </div>
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION C — MRR Snapshot Widget
// ═══════════════════════════════════════════════════════════════════════════
function MRRWidget() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/api/accounts/mrr-summary').then(({ data }) => {
      setSummary(data);
      setLoading(false);
    });
  }, []);

  if (loading) return <Card className="p-6"><Spinner /></Card>;

  const total = summary?.total_mrr || 0;
  const activeSubs = summary?.active_subscriptions || 0;

  return (
    <Card>
      <CardHeader title="MRR At A Glance" subtitle="Total active subscriptions from your outreach" />
      <div className="p-5 grid grid-cols-3 gap-4">
        <div className="col-span-3 sm:col-span-1">
          <div className="text-3xl font-bold text-slate-900">{money(total)}</div>
          <div className="text-xs text-slate-500 mt-1">Current MRR</div>
        </div>
        <div>
          <div className="text-2xl font-semibold text-slate-800">{activeSubs}</div>
          <div className="text-xs text-slate-500 mt-1">Active subs</div>
        </div>
        <div>
          <div className="text-2xl font-semibold text-slate-800">{money(total * 12)}</div>
          <div className="text-xs text-slate-500 mt-1">ARR run-rate</div>
        </div>
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION C+ — MRR Trend (monthly)
// ═══════════════════════════════════════════════════════════════════════════
function MRRTrendWidget() {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api(`/api/accounts/mrr-history?months=6&offer_slug=${SOURCEPOINT_OFFER_SLUG}`).then(({ data }) => {
      setHistory(data);
      setLoading(false);
    });
  }, []);

  if (loading) return <Card className="p-6"><Spinner /></Card>;
  if (!history?.months?.length) {
    return (
      <Card>
        <CardHeader title="Monthly MRR Trend" subtitle="6-month rollup (SourcePoint only)" />
        <div className="p-8 text-center text-sm text-slate-500">No MRR history yet. First subscriptions will show up here.</div>
      </Card>
    );
  }

  const current = history.months[history.months.length - 1] || {};
  const previous = history.months[history.months.length - 2] || {};
  const delta = (current.mrr_run_rate || 0) - (previous.mrr_run_rate || 0);
  const deltaPct = previous.mrr_run_rate ? (delta / previous.mrr_run_rate) * 100 : 0;

  return (
    <Card>
      <CardHeader
        title="Monthly MRR Trend"
        subtitle="SourcePoint website MRR · last 6 months"
      />
      <div className="p-5">
        <div className="flex items-baseline gap-3 mb-4">
          <div className="text-3xl font-bold text-slate-900">{money(current.mrr_run_rate)}</div>
          <div className={`text-sm flex items-center gap-1 ${delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {delta >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {delta >= 0 ? '+' : ''}{money(delta)} ({deltaPct.toFixed(0)}%) MoM
          </div>
        </div>
        <div className="w-full h-52">
          <ResponsiveContainer>
            <LineChart data={history.months}>
              <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
              <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => money(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="mrr_run_rate" name="MRR run-rate" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="net_new_mrr"  name="Net new" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-4 text-xs">
          <div><div className="text-slate-500">New this month</div><div className="font-semibold text-emerald-600">{money(current.new_mrr)}</div></div>
          <div><div className="text-slate-500">Churned</div><div className="font-semibold text-red-600">-{money(current.churned_mrr)}</div></div>
          <div><div className="text-slate-500">Active subs</div><div className="font-semibold text-slate-800">{current.active_subs}</div></div>
        </div>
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION D — HandoffQueueWidget
// ═══════════════════════════════════════════════════════════════════════════
const HANDOFF_STATUS_COLOR = {
  PENDING:   'bg-amber-100 text-amber-700',
  CONTACTED: 'bg-blue-100 text-blue-700',
  WON:       'bg-emerald-100 text-emerald-700',
  LOST:      'bg-slate-100 text-slate-600',
};

function HandoffQueueWidget({ vaId, refreshTrigger }) {
  const [handoffs, setHandoffs] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams({ owner: vaId });
    if (filter) q.set('status', filter);
    const { data } = await api(`/api/admin/va-handoff-queue?${q}`);
    if (data) {
      setHandoffs(data.handoffs || []);
      setCounts(data.counts_by_status || {});
    }
    setLoading(false);
  }, [vaId, filter]);

  useEffect(() => { load(); }, [load, refreshTrigger]);

  return (
    <Card>
      <CardHeader
        title="My Hot Handoffs to Jamie"
        subtitle={`${handoffs.length} shown · Jamie's response status tracked here`}
        action={
          <div className="flex items-center gap-1">
            {['', 'PENDING', 'CONTACTED', 'WON', 'LOST'].map((s) => (
              <button
                key={s || 'all'}
                onClick={() => setFilter(s)}
                className={`px-2 py-1 text-xs rounded ${filter === s ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                {s || 'All'}{s && counts[s] > 0 && ` · ${counts[s]}`}
              </button>
            ))}
          </div>
        }
      />
      {loading ? (
        <div className="p-10 flex justify-center"><Spinner /></div>
      ) : handoffs.length === 0 ? (
        <div className="p-10 text-center text-sm text-slate-500 flex flex-col items-center gap-2">
          <Flame className="w-6 h-6 text-slate-300" />
          <p>No handoffs yet. Mark a lead <span className="font-semibold">🔥 Hot handoff</span> on a call to escalate to Jamie.</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {handoffs.map((h) => (
            <div key={h.id} className="px-5 py-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-800 truncate">{h.company || h.contact_name || 'unknown'}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${HANDOFF_STATUS_COLOR[h.status] || 'bg-slate-100'}`}>{h.status}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3">
                  {h.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {h.phone}</span>}
                  {h.city && <span>{h.city}</span>}
                  <span>Sent {timeAgo(h.created_at)}</span>
                </div>
                {h.va_note && <div className="text-xs text-slate-500 mt-1 italic">"{h.va_note}"</div>}
                {h.jamie_note && (
                  <div className="mt-1 bg-blue-50 border border-blue-100 rounded px-2 py-1 text-xs text-blue-800">
                    Jamie: {h.jamie_note}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SECTION E — OutreachTrackerWidget
// ═══════════════════════════════════════════════════════════════════════════
function OutreachTrackerWidget({ vaId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('team'); // team | me

  const load = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams({ days: '14' });
    if (scope === 'me' && vaId) q.set('va_id', vaId);
    const { data } = await api(`/api/admin/outreach-stats?${q}`);
    if (data) setStats(data);
    setLoading(false);
  }, [scope, vaId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Card className="p-6"><Spinner /></Card>;
  if (!stats) return null;

  const today = stats.days[stats.days.length - 1] || {};

  return (
    <Card>
      <CardHeader
        title="Daily Outreach Tracker"
        subtitle="Emails + calls per day, last 14 days"
        action={
          <div className="flex items-center gap-1">
            <button onClick={() => setScope('team')} className={`px-2 py-1 text-xs rounded ${scope === 'team' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`}>Team</button>
            <button onClick={() => setScope('me')}   className={`px-2 py-1 text-xs rounded ${scope === 'me'   ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600'}`}>Me</button>
          </div>
        }
      />
      <div className="p-5">
        <div className="grid grid-cols-4 gap-3 mb-4 text-center">
          <div><div className="text-xs text-slate-500 mb-1">Emailed today</div><div className="text-lg font-bold text-sky-700">{today.emailed || 0}</div></div>
          <div><div className="text-xs text-slate-500 mb-1">Called today</div><div className="text-lg font-bold text-indigo-700">{today.called || 0}</div></div>
          <div><div className="text-xs text-slate-500 mb-1">Previews texted</div><div className="text-lg font-bold text-emerald-700">{today.previews_texted || 0}</div></div>
          <div><div className="text-xs text-slate-500 mb-1">Hot handoffs</div><div className="text-lg font-bold text-red-600">{today.hot_handoffs || 0}</div></div>
        </div>
        <div className="w-full h-56">
          <ResponsiveContainer>
            <BarChart data={stats.days}>
              <XAxis dataKey="date" stroke="#94a3b8" fontSize={10} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#94a3b8" fontSize={11} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="emailed" stackId="a" name="Emailed" fill="#0ea5e9" />
              <Bar dataKey="called"  stackId="a" name="Called"  fill="#6366f1" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-4 gap-3 text-xs">
          <div><div className="text-slate-500">14d emailed</div><div className="font-semibold">{stats.totals.emailed}</div></div>
          <div><div className="text-slate-500">14d called</div><div className="font-semibold">{stats.totals.called}</div></div>
          <div><div className="text-slate-500">14d previews</div><div className="font-semibold">{stats.totals.previews_texted}</div></div>
          <div><div className="text-slate-500">14d handoffs</div><div className="font-semibold">{stats.totals.hot_handoffs}</div></div>
        </div>
      </div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN — VADashboard
// ═══════════════════════════════════════════════════════════════════════════
export default function VADashboard() {
  const [vaId, setVaId] = useVAIdentity();
  const [toasts, toast] = useToasts();
  const [refreshCounter, setRefreshCounter] = useState(0);

  const bump = () => setRefreshCounter((c) => c + 1);

  if (!vaId) return <VAIdentityGate onSet={setVaId} />;

  return (
    <div className="min-h-screen bg-slate-100 text-slate-800">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-indigo-600 text-white flex items-center justify-center font-bold text-sm">VA</div>
          <div>
            <div className="font-semibold text-slate-900 text-sm">SourcePoint VA Dashboard</div>
            <div className="text-xs text-slate-500">Signed in as {vaId}</div>
          </div>
        </div>
        <button
          onClick={() => { localStorage.removeItem('va_id'); setVaId(''); }}
          className="text-xs text-slate-500 hover:text-slate-800"
        >
          Sign out
        </button>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto p-6 space-y-6">
        <CallListWidget vaId={vaId} toast={toast} refreshTrigger={refreshCounter} onLog={bump} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TriggerScrapeWidget toast={toast} onScraped={bump} />
          <MRRWidget />
        </div>

        <MRRTrendWidget />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <HandoffQueueWidget vaId={vaId} refreshTrigger={refreshCounter} />
          <OutreachTrackerWidget vaId={vaId} />
        </div>

        <div className="text-center text-xs text-slate-400 py-4">
          SourcePoint VA · Total Trust Consulting
        </div>
      </main>

      <Toasts items={toasts} />
    </div>
  );
}
